/**
 * Search, sort and paging for the Einheiten list (Units redesign, Slice 8).
 *
 * Pure on purpose: everything here is a function of its arguments, so the
 * rules can be tested without rendering a page, and the page keeps only the
 * state that drives them.
 *
 * WHERE THIS SITS IN THE PIPELINE, and the order matters:
 *
 *   loaded units
 *     -> matchesUnitSearch + property filter   (both segments at once)
 *     -> the page's existing raw type split    (unchanged, see below)
 *     -> sortUnitList                          (per segment)
 *     -> usePagination                         (per segment)
 *     -> UnitListRow
 *
 * Tab counts are taken BEFORE paging, so a tab reads "14" while its first page
 * shows ten. Paging is a view onto a population, not the population.
 *
 * THE SEGMENT SPLIT IS NOT DONE HERE, deliberately. The page still splits on a
 * raw `item.type` comparison that diverges from `normalizeUnitTypeLabel`, and
 * this slice does not reopen that. Search gained the visible id; segmentation
 * kept its bug.
 */
import { getDisplayUnitId } from "../../../utils/unitDisplayId";
import {
  getUnitOccupancyStatus,
  formatOccupancyStatusDe,
} from "../../../utils/unitOccupancyStatus";
import { landlordDisplayForId } from "./apartmentsHelpers";
import { buildUnitListItem } from "./unitListModel";

/** Lowercased, trimmed, never undefined. */
function norm(value) {
  return String(value ?? "").toLowerCase().trim();
}

/**
 * Does this unit match the search term?
 *
 * EVERY FIELD THE PAGE SEARCHED BEFORE IS STILL SEARCHED, plus the one that was
 * missing: the id the list actually shows. Typing "CL-001" used to find
 * nothing — the id field searched `unitId || id`, the UUID, while the row
 * displayed the short id. The placeholder promised "Suche nach Unit ID" and
 * meant a value no one can see.
 *
 * `getDisplayUnitId` is called WITHOUT an index on purpose. With one it falls
 * back to a positional label ("CL-003" = third in the array), and a positional
 * label is a property of where a unit sits in a filtered list, not of the unit:
 * searching for it would change the positions it was derived from. So the
 * persisted short id is searchable and the positional stand-in is not, which
 * is the honest split.
 */
export function matchesUnitSearch(unit, term, { rooms, tenancies, landlords } = {}) {
  const needle = norm(term);
  if (!needle) return true;
  if (!unit) return false;

  const occupancy = formatOccupancyStatusDe(
    getUnitOccupancyStatus(unit, rooms, tenancies)
  );

  const haystacks = [
    getDisplayUnitId(unit), // the visible id — the field this slice adds
    unit.unitId || unit.id, // the raw UUID, searchable as before
    unit.place || unit.city,
    unit.zip,
    unit.address,
    unit.type,
    occupancy,
    unit.property_title,
    landlordDisplayForId(landlords, unit.landlord_id),
    unit.landlord_id,
  ];

  return haystacks.some((value) => {
    const hay = norm(value);
    return hay !== "" && hay.includes(needle);
  });
}

/** The sort choices, in the order the select offers them. */
export const UNIT_SORT_OPTIONS = [
  { id: "default", label: "Standard" },
  { id: "id_asc", label: "Unit ID aufsteigend" },
  { id: "id_desc", label: "Unit ID absteigend" },
  { id: "address_asc", label: "Adresse A–Z" },
  { id: "revenue_desc", label: "Einnahmen absteigend" },
];

export const DEFAULT_UNIT_SORT = "default";

/** Swiss-German collation, built once rather than per comparison. */
const COLLATOR = new Intl.Collator("de-CH", { sensitivity: "base", numeric: true });

/**
 * Compare two possibly-missing strings, missing last.
 *
 * "Missing last" is the whole point: a unit without an address should not lead
 * an A–Z list just because the empty string sorts first.
 */
function compareText(a, b) {
  const left = String(a ?? "").trim();
  const right = String(b ?? "").trim();
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return COLLATOR.compare(left, right);
}

/**
 * Compare two ids descending, still missing-last.
 *
 * Not `-compareText(...)`: negating that would also negate the missing-last
 * rule and float ids-without-a-value to the top of the list.
 */
function compareTextDesc(a, b) {
  const left = String(a ?? "").trim();
  const right = String(b ?? "").trim();
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return COLLATOR.compare(right, left);
}

/** Compare two possibly-null numbers, descending, missing last. */
function compareNumberDesc(a, b) {
  const left = typeof a === "number" && Number.isFinite(a) ? a : null;
  const right = typeof b === "number" && Number.isFinite(b) ? b : null;
  // A missing revenue is NOT zero. Treating it as zero would file an unknown
  // between a loss and a small gain and make it look measured.
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return right - left;
}

/** The em dash the view model uses for an unresolvable id — not a real id. */
const NO_DISPLAY_ID = "—";

/**
 * Sort a segment's units.
 *
 * DECORATE, SORT, UNDECORATE: the view model is built ONCE per unit and the
 * raw units come back in the new order, so callers (and the two list
 * components) are untouched. Building the item rather than re-deriving the
 * keys matters most for money — revenue is segment-dependent, and that rule
 * belongs in exactly one place.
 *
 * Returns a NEW array; the input is never mutated. `Array.prototype.sort` is
 * stable, so "Standard" and equal keys keep source order.
 */
export function sortUnitList(units, sortId, { rooms, tenancies } = {}) {
  const list = Array.isArray(units) ? units : [];
  if (sortId === DEFAULT_UNIT_SORT || !sortId) return list.slice();

  const decorated = list.map((unit, index) => {
    const item = buildUnitListItem({ unit, rooms, tenancies });
    const displayId = item?.displayId;
    return {
      unit,
      index,
      displayId: displayId === NO_DISPLAY_ID ? "" : displayId,
      address: item?.addressLine1,
      revenue: item?.currentRevenueChf ?? null,
    };
  });

  const comparators = {
    id_asc: (a, b) => compareText(a.displayId, b.displayId),
    id_desc: (a, b) => compareTextDesc(a.displayId, b.displayId),
    address_asc: (a, b) => compareText(a.address, b.address),
    revenue_desc: (a, b) => compareNumberDesc(a.revenue, b.revenue),
  };
  const compare = comparators[sortId];
  if (!compare) return list.slice();

  decorated.sort((a, b) => {
    const result = compare(a, b);
    // Source order breaks ties, which is what keeps every sort stable and
    // "Standard" meaningful.
    return result !== 0 ? result : a.index - b.index;
  });

  return decorated.map((entry) => entry.unit);
}
